import React, {useRef} from "react";
import {ApiArticle, ApiBlogData} from "#/src/definitions/ApiDefinitions";
import {NewestArticle} from "#/src/components/NewestArticle";
import {HotArticle} from "#/src/pages/blog/HotArticle";
import {ArticleCard} from "#/src/components/ArticleCard";
import {Swiper, SwiperSlide} from "swiper/react";
import {useData} from "vike-react/useData";
import {Swiper as SwiperCore} from "swiper/types";
import {Sort} from "#/src/utils/sort-utils";
import {OlderArticles} from "#/src/pages/blog/OlderArticles";
import {DataWrapped, PaginatedRequest} from "#/src/definitions/AppDefinitions";
import {LoaderSvg} from "#/src/components/LoaderSvg";
import styled from "@emotion/styled";
import {LoadableButton} from "#/src/components/LoadableButton";

const LoadMoreButton = styled(LoadableButton)`
  min-width: 12em;
`;

interface Props {
  query: {
    enabled: boolean,
    isPending: boolean,
    error: boolean,
    pageData: PaginatedRequest<ApiArticle> | null
  },
  onPageFetch: () => Promise<void>
}

export function Content({query, onPageFetch}: Props) {
  const {data: blogData} = useData() as DataWrapped<ApiBlogData>;
  const swiperRef = useRef<SwiperCore>();

  function renderQueriedBlog() {
    if (query.isPending) {
      return <div className={"col-12 text-center text-loading"}><LoaderSvg/> Ładuję zawartość...</div>
    }
    if (query.error) {
      return <div className={"col-12 text-center text-danger"}>Nie udało się załadować...</div>
    }
    const queriedData = Sort.byDate(query.pageData!.data);
    if(queriedData.length == 0) {
      return <div className={"col-12 text-center text-white"}>Brak zawartości</div>
    }
    return queriedData.map((article: ApiArticle) => {
      return <div key={article.id} className="col-xl-3 col-sm-6 col-md-4"><ArticleCard article={article}/></div>
    });
  }
  function renderFetchButton() {
    if (!query.pageData || query.pageData.maxPages <= query.pageData.page) {
      return <></>
    }
    return <div className={"row justify-content-center"}>
      <div className={"text-center"}>
        <LoadMoreButton className={"btn btn-secondary"} onClick={onPageFetch}>Załaduj więcej</LoadMoreButton>
      </div>
    </div>
  }

  function renderBlog() {
    const hotArticles = Sort.byDate(blogData.hotArticles);
    const freshArticles = Sort.byDate(blogData.freshArticles);
    return <React.Fragment>
      <section className="front-header mt-9">
        <div className="container-xxl">
          <div className="row">
            <div className="col-xl-8 col-lg-8 col-md-12">
              <h2 className="basic-headline mb-4">Najnowszy artykuł</h2>
              <NewestArticle article={blogData.latestArticle} size={"medium"}/>
            </div>
            <div className="col-xl-4 col-lg-4 col-md-12">
              <h2 className="basic-headline mb-4">
                Popularne artykuły
              </h2>
              {hotArticles.slice(0, 5).map((hotArticle: ApiArticle) => <HotArticle key={hotArticle.id} article={hotArticle}/>)}
            </div>
          </div>
        </div>
      </section>

      <section className="last-article mb-6">
        <div className="container-xxl">
          <div className="row border-b-dark">
            <div className="col-xl-12">
              <div className="title-arrow">
                <h2 className="basic-headline">
                  Jeszcze na czasie!
                </h2>
                <div className="arrow-group ms-3">
                  <button type="button" className="prev" onClick={() => swiperRef.current?.slidePrev()} aria-label={"Wstecz"}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.5 5L7.5 10L12.5 15" stroke="#EEF5F8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button type="button" className="next" onClick={() => swiperRef.current?.slideNext()} aria-label={"Do przodu"}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7.5 15L12.5 10L7.5 5" stroke="#EEF5F8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div className="col-xl-12">
              <Swiper
                direction={"horizontal"}
                loop={true}
                grabCursor={true}
                slidesPerView={"auto"}
                spaceBetween={30}
                navigation={{nextEl: '.next', prevEl: '.prev'}}
                breakpoints={{
                  340: {slidesPerView: 1.5, spaceBetween: 20},
                  540: {slidesPerView: 2.15, spaceBetween: 30},
                  720: {slidesPerView: 2.15, spaceBetween: 30,},
                  960: {slidesPerView: 3.2, spaceBetween: 30},
                  1140: {slidesPerView: 3, spaceBetween: 30},
                  1290: {slidesPerView: 4, spaceBetween: 30},
                }}
                onBeforeInit={(swiper) => swiperRef.current = swiper}
              >
                {freshArticles.map((article: ApiArticle) => <SwiperSlide key={article.id}><ArticleCard article={article} lazy/></SwiperSlide>)}
              </Swiper>
            </div>
          </div>
        </div>
      </section>
      <OlderArticles/>
    </React.Fragment>
  }

  if (!query.enabled) {
    return renderBlog();
  }
  return <section className="front-header mt-9">
    <div className="container-xxl">
      <div className={"row"}>
        {renderQueriedBlog()}
        {renderFetchButton()}
      </div>
    </div>
  </section>
}